/**
 * Подраздел «Все» страницы «Аналитика» (US-04.1) — сводная витрина:
 * переиспользует ключевые виджеты тематических подразделов через их
 * `summary`-режим (единый источник правды, FR-3 — состав ключевых
 * виджетов см. FR-2.2), сгруппированные по темам в порядке подразделов
 * (FR-2.1). Собственной верхней плашки у витрины нет (решённый вопрос
 * US-04.1); заголовки тем — штатные заголовки секций.
 *
 * Клик по теме открывает соответствующий тематический подраздел
 * (FR-4.1) — через onNavigate (чип панели подразделов); drill-through
 * до первичных данных выполняется уже из подраздела (FR-4.2, вне
 * текущей вёрстки).
 *
 * Данные — один запрос `/analytics/all` через AnalyticsAllProvider:
 * восемь секций больше не тянут по своему эндпоинту (браузер держит 6
 * соединений на хост — последние разделы вставали в очередь). Пока ответ
 * в полёте, секции показывают каркас-скелет.
 */
import type { ComponentType, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { AgricultureSection } from './AgricultureSection';
import { ConstructionSection } from './ConstructionSection';
import { EconomySection } from './EconomySection';
import { InfrastructureSection } from './InfrastructureSection';
import { LandFundSection } from './LandFundSection';
import { PopulationSection } from './PopulationSection';
import { SnpNeedsSection } from './SnpNeedsSection';
import { SocialSection } from './SocialSection';
import { useAnalyticsSectionPresent } from '../analytics-visibility';
import type { AnalyticsFilter } from '../analytics.page';
import { AnalyticsAllProvider } from '../AnalyticsAllContext';

/** Темы витрины в порядке подразделов (US-04.1 FR-2.1). */
const THEMES: Array<{
  key: AnalyticsFilter;
  Section: ComponentType<{ summary?: boolean }>;
}> = [
  { key: 'population', Section: PopulationSection },
  { key: 'economy', Section: EconomySection },
  { key: 'social', Section: SocialSection },
  { key: 'agriculture', Section: AgricultureSection },
  { key: 'landFund', Section: LandFundSection },
  { key: 'infrastructure', Section: InfrastructureSection },
  { key: 'construction', Section: ConstructionSection },
  { key: 'snpNeeds', Section: SnpNeedsSection },
];

interface Props {
  /** Переход в тематический подраздел по клику по теме (FR-4.1). */
  onNavigate: (filter: AnalyticsFilter) => void;
}

export const AllSection = ({ onNavigate }: Props) => {
  const { t } = useTranslation();
  // Тема, выключенная супер-админом, скрывается и из сводной (US-09 FR-1).
  const sectionPresent = useAnalyticsSectionPresent();
  const themes = THEMES.filter(({ key }) => sectionPresent(key));

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    filter: AnalyticsFilter,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onNavigate(filter);
    }
  };

  return (
    // Один запрос /analytics/all на всю витрину: секции внутри провайдера
    // берут готовые слайсы и своих запросов не делают (кроме разделов,
    // которых в общем ответе ещё нет — они догружаются сами).
    <AnalyticsAllProvider>
    <div className="space-y-6" data-testid="analytics-all">
      {themes.map(({ key, Section }) => (
        <div
          key={key}
          role="button"
          tabIndex={0}
          aria-label={t(`analytics.filters.${key}`)}
          data-testid={`analytics-all-${key}`}
          onClick={() => onNavigate(key)}
          onKeyDown={(event) => handleKeyDown(event, key)}
          className="cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <Section summary />
        </div>
      ))}
    </div>
    </AnalyticsAllProvider>
  );
};
